import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import apiFetch, {
  API_BASE,
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRY_UPDATED_EVENT,
  SESSION_EXPIRED_MESSAGE,
  SESSION_NOTICE_KEY,
  notifySessionExpired,
  resetSessionExpiredState,
} from './utils/apiFetch';
import {
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import Router from './components/GuardedHashRouter';
import { Box, Typography, ThemeProvider, createTheme } from '@mui/material';
import SessionExpiryDialog from './components/SessionExpiryDialog';
import { canAccessPath, getDefaultPath } from './utils/accessControl';
import {
  clearUnsavedChanges,
  protectUnsavedChangesBeforeUnload
} from './utils/unsavedChanges';
import { loadRightEmrApplication, loadRightMatApplication } from './utils/applicationPreload';
import { importWithChunkRecovery } from './utils/lazyChunkRecovery';

// Keep each application workspace out of the login shell. React will download
// only the selected tenant application, and HashRouter continues to own the
// URL so existing bookmarks and application-switcher links are unchanged.
const MainPage = lazy(() => import('./components/MainPage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Encounters = lazy(() => import('./components/Encounters'));
const EncounterDetail = lazy(() => import('./components/EncounterDetail'));
const ClaimRejection = lazy(() => import('./components/ClaimRejection'));
const ClaimRejectionDetail = lazy(() => import('./components/ClaimRejectionDetail'));
const BillingEra = lazy(() => import('./components/BillingEra'));
const Simulator = lazy(() => import('./components/Simulator'));
const Director = lazy(() => import('./components/Director'));
const ProviderCredentialPage = lazy(() => import('./components/provider-credential/ProviderCredentialPage'));
const ABASphereWorkspace = lazy(() => importWithChunkRecovery(
  () => import('./components/ABASphereWorkspace')
));
const MedicalAssist = lazy(() => import('./components/MedicalAssist'));
const PriorAuthorization = lazy(() => import('./components/PriorAuthorization'));
const PriorAuthTracking = lazy(() => import('./components/PriorAuthTracking'));
const Methadone = lazy(loadRightMatApplication);
const RightEMRWorkspace = lazy(loadRightEmrApplication);
const PlatformAdmin = lazy(() => import('./components/PlatformAdmin'));
const AssistantChat = lazy(() => import('./components/AssistantChat'));

const theme = createTheme({
  typography: {
    fontFamily: 'sans-serif',
  },
});

const USER_INFO_KEY = 'userInfo';
const SESSION_RESTORE_MESSAGE = 'Your previous session could not be verified. Please log in again.';

const validFutureExpiration = (value, clockOffsetMilliseconds = 0) => {
  const expiration = Date.parse(value || '');
  const offset = Number.isFinite(Number(clockOffsetMilliseconds)) ? Number(clockOffsetMilliseconds) : 0;
  return Number.isFinite(expiration) && expiration > Date.now() + offset
    ? new Date(expiration).toISOString()
    : '';
};

const serverClockOffset = (serverTime, fallback = 0) => {
  const serverTimestamp = Date.parse(serverTime || '');
  return Number.isFinite(serverTimestamp)
    ? serverTimestamp - Date.now()
    : (Number.isFinite(Number(fallback)) ? Number(fallback) : 0);
};

const RouteLoadingFallback = () => (
  <Box role="status" aria-live="polite" sx={{ p: 4 }}>
    <Typography variant="body2" color="text.secondary">Loading application...</Typography>
  </Box>
);

// Restores login state from localStorage on page load.
// The JWT itself lives in an httpOnly cookie (inaccessible to JS); only
// the non-sensitive display info (userId, role, etc.) is in localStorage.
function restoreSession() {
  try {
    const stored = localStorage.getItem(USER_INFO_KEY);
    if (!stored) return { isLoggedIn: false, user: null };
    const user = JSON.parse(stored);
    if (!user?.userId) return { isLoggedIn: false, user: null };
    if (!user.tenantApplications) {
      localStorage.removeItem(USER_INFO_KEY);
      return { isLoggedIn: false, user: null };
    }
    const sessionClockOffsetMilliseconds = serverClockOffset(
      '',
      user.sessionClockOffsetMilliseconds
    );
    const sessionExpiresAt = validFutureExpiration(
      user.sessionExpiresAt,
      sessionClockOffsetMilliseconds
    );
    if (!sessionExpiresAt) {
      localStorage.removeItem(USER_INFO_KEY);
      return { isLoggedIn: false, user: null, notice: SESSION_RESTORE_MESSAGE };
    }
    return {
      isLoggedIn: true,
      user: {
        ...user,
        sessionExpiresAt,
        sessionClockOffsetMilliseconds,
        sessionCanExtend: user.sessionCanExtend !== false,
      },
    };
  } catch {
    localStorage.removeItem(USER_INFO_KEY);
    return { isLoggedIn: false, user: null };
  }
}

const App = () => {
  useEffect(() => {
    const handleBeforeUnload = event => {
      protectUnsavedChangesBeforeUnload(event);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  const [restored] = useState(restoreSession);
  const [isLoggedIn, setIsLoggedIn] = useState(restored.isLoggedIn);
  const [user, setUser] = useState(restored.user);
  const [sessionNotice, setSessionNotice] = useState(restored.notice || '');

  const clearClientSession = useCallback((message = '') => {
    clearUnsavedChanges();
    try {
      localStorage.removeItem(USER_INFO_KEY);
    } catch {
      // React state still removes protected content when storage is unavailable.
    }
    setUser(null);
    setIsLoggedIn(false);
    setSessionNotice(message);
    try {
      sessionStorage.removeItem(SESSION_NOTICE_KEY);
    } catch {
      // Session storage can be unavailable in restricted browser modes.
    }
  }, []);

  const applySessionExpiry = useCallback((expiresAt, sessionDetails = {}) => {
    if (!Number.isFinite(Date.parse(expiresAt || ''))) return false;
    setUser(current => {
      if (!current) return current;
      const sessionClockOffsetMilliseconds = serverClockOffset(
        sessionDetails.serverTime,
        current.sessionClockOffsetMilliseconds
      );
      const normalizedExpiration = validFutureExpiration(
        expiresAt,
        sessionClockOffsetMilliseconds
      );
      if (!normalizedExpiration) {
        window.setTimeout(notifySessionExpired, 0);
        return current;
      }
      const sessionCanExtend = typeof sessionDetails.canExtend === 'boolean'
        ? sessionDetails.canExtend
        : current.sessionCanExtend !== false;
      if (
        current.sessionExpiresAt === normalizedExpiration &&
        current.sessionClockOffsetMilliseconds === sessionClockOffsetMilliseconds &&
        current.sessionCanExtend === sessionCanExtend
      ) return current;
      const nextUser = {
        ...current,
        sessionExpiresAt: normalizedExpiration,
        sessionClockOffsetMilliseconds,
        sessionCanExtend,
      };
      try {
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(nextUser));
      } catch {
        // Keep the in-memory timer authoritative in restricted browser modes.
      }
      return nextUser;
    });
    return true;
  }, []);

  useEffect(() => {
    const expireSession = event => {
      clearClientSession(event?.detail?.message || SESSION_EXPIRED_MESSAGE);
    };

    const updateSessionExpiry = event => applySessionExpiry(event?.detail?.expiresAt, {
      canExtend: event?.detail?.canExtend,
      serverTime: event?.detail?.serverTime,
    });

    const synchronizeSession = event => {
      if (event.storageArea !== localStorage || event.key !== USER_INFO_KEY) return;
      if (!event.newValue) {
        clearClientSession('You were logged out in another browser tab.');
        return;
      }
      try {
        const synchronizedUser = JSON.parse(event.newValue);
        const sessionClockOffsetMilliseconds = serverClockOffset(
          '',
          synchronizedUser?.sessionClockOffsetMilliseconds
        );
        const sessionExpiresAt = validFutureExpiration(
          synchronizedUser?.sessionExpiresAt,
          sessionClockOffsetMilliseconds
        );
        if (!synchronizedUser?.userId || !synchronizedUser?.tenantApplications || !sessionExpiresAt) {
          clearClientSession(SESSION_EXPIRED_MESSAGE);
          return;
        }
        resetSessionExpiredState();
        setSessionNotice('');
        setUser({
          ...synchronizedUser,
          sessionExpiresAt,
          sessionClockOffsetMilliseconds,
          sessionCanExtend: synchronizedUser.sessionCanExtend !== false,
        });
        setIsLoggedIn(true);
      } catch {
        clearClientSession(SESSION_EXPIRED_MESSAGE);
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, expireSession);
    window.addEventListener(SESSION_EXPIRY_UPDATED_EVENT, updateSessionExpiry);
    window.addEventListener('storage', synchronizeSession);
    try {
      const pendingNotice = sessionStorage.getItem(SESSION_NOTICE_KEY);
      if (pendingNotice) expireSession({ detail: { message: pendingNotice } });
    } catch {
      // Event-based handling remains available when session storage is disabled.
    }
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, expireSession);
      window.removeEventListener(SESSION_EXPIRY_UPDATED_EVENT, updateSessionExpiry);
      window.removeEventListener('storage', synchronizeSession);
    };
  }, [applySessionExpiry, clearClientSession]);

  const handleLogin = useCallback((userData) => {
    // userData comes from the server response; token is in httpOnly cookie
    const sessionClockOffsetMilliseconds = serverClockOffset(userData?.sessionServerTime);
    const sessionExpiresAt = validFutureExpiration(
      userData?.sessionExpiresAt,
      sessionClockOffsetMilliseconds
    );
    if (!sessionExpiresAt) return false;
    const sessionUser = {
      ...userData,
      sessionExpiresAt,
      sessionClockOffsetMilliseconds,
      sessionCanExtend: userData.sessionCanExtend !== false,
    };
    resetSessionExpiredState();
    try {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(sessionUser));
    } catch {
      // The active tab can continue securely even when persistence is disabled.
    }
    setSessionNotice('');
    setUser(sessionUser);
    setIsLoggedIn(true);
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    resetSessionExpiredState();
    // Ask the server to clear the httpOnly cookie
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    clearClientSession('');
  }, [clearClientSession]);

  const handleExtendSession = useCallback(async () => {
    const rightEmrSession = /^#\/rightemr(?:[/?]|$)/i.test(window.location.hash);
    const response = await apiFetch(`${API_BASE}/auth/extend-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(rightEmrSession ? { body: JSON.stringify({ application: 'rightemr' }) } : {}),
      retry: false,
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 401) {
      notifySessionExpired();
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
    const expirationApplied = data.sessionExpiresAt
      ? applySessionExpiry(data.sessionExpiresAt, {
        serverTime: data.serverTime,
        canExtend: data.canExtend,
      })
      : false;
    if (!response.ok) {
      const requiresSignIn = response.status === 409 && (
        data.canExtend === false || data.reauthenticationRequired || data.code === 'REAUTHENTICATION_REQUIRED'
      );
      throw new Error(requiresSignIn
        ? 'This session has reached its maximum duration. Save your work and log in again.'
        : (data.message || 'The session could not be extended. Please try again.'));
    }
    if (!expirationApplied) {
      throw new Error('The server did not return a valid session expiration. Please log in again.');
    }
  }, [applySessionExpiry]);

  const protectedElement = (path, element) => {
    if (!isLoggedIn) return <Navigate to="/" replace />;
    if (!canAccessPath(user, path)) return <Navigate to={getDefaultPath(user)} replace />;
    return element;
  };

  const accessUnavailableElement = (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" fontWeight={700}>No application screens are available for this account.</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Ask a tenant administrator to verify the tenant applications and your role or application-specific assignment.
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{ m: 0, p: 0 }}>
      <Router>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
          <Route
            path="/"
            element={isLoggedIn ? <Navigate to={getDefaultPath(user)} replace /> : <MainPage onLoginSuccess={handleLogin} sessionMessage={sessionNotice} />}
          />
          <Route
            path="/platform-admin"
            element={protectedElement('/platform-admin', <PlatformAdmin user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/RCM Insights"
            element={protectedElement('/dashboard', <Dashboard user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/dashboard"
            element={protectedElement('/dashboard', <Dashboard user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/encounters"
            element={protectedElement('/encounters', <Encounters user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/EncounterDetail/:encounterId"
            element={protectedElement('/EncounterDetail', <EncounterDetail user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/EncounterDetail/claim/:claimId"
            element={protectedElement('/EncounterDetail', <EncounterDetail user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/simulation"
            element={protectedElement('/simulation', <Simulator user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/claim-rejection"
            element={protectedElement('/claim-rejection', <ClaimRejection user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/claim-rejection/:type/:value"
            element={protectedElement('/claim-rejection', <ClaimRejectionDetail user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/billing/eras"
            element={protectedElement('/billing/eras', <BillingEra user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/Director"
            element={protectedElement('/Director', <Director user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/provider-credential"
            element={protectedElement('/provider-credential', <ProviderCredentialPage user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/aba"
            element={protectedElement('/aba', <ABASphereWorkspace user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/medical-assist"
            element={protectedElement('/medical-assist', <MedicalAssist user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/prior-authorization"
            element={protectedElement('/prior-authorization', <PriorAuthorization user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/prior-authorization/tracking"
            element={protectedElement('/prior-authorization/tracking', <PriorAuthTracking user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/methadone"
            element={protectedElement('/methadone', <Methadone user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/rightemr/*"
            element={protectedElement('/rightemr', <RightEMRWorkspace user={user} onLogout={handleLogout} />)}
          />
          <Route
            path="/access-unavailable"
            element={isLoggedIn ? accessUnavailableElement : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to={isLoggedIn ? getDefaultPath(user) : '/'} replace />} />
          </Routes>
        </Suspense>
        {isLoggedIn && canAccessPath(user, '/dashboard') && (
          <Suspense fallback={null}>
            <AssistantChat />
          </Suspense>
        )}
        {isLoggedIn && user?.sessionExpiresAt && (
          <SessionExpiryDialog
            expiresAt={user.sessionExpiresAt}
            canExtend={user.sessionCanExtend !== false}
            clockOffsetMilliseconds={user.sessionClockOffsetMilliseconds}
            onExtend={handleExtendSession}
            onLogout={handleLogout}
            onExpired={notifySessionExpired}
          />
        )}
      </Router>
    </Box>
    </ThemeProvider>
  );
};

export default App;
